import { ArrowDownTrayIcon, ArrowPathIcon, BuildingStorefrontIcon } from '@heroicons/react/24/outline';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../store/store';
import type { CardHolderListItem, CardHolderListResponse } from '../../../utils/cardHolders';
import {
  CARD_HOLDER_EXPORT_HEADERS,
  CARD_HOLDER_EXPORT_WIDTHS,
  formatIsoDay,
  toCardHolderExportRow,
  toCardHolderListItems,
} from '../../../utils/cardHolders';
import { downloadWorkbook, EXPORT_CELL_STYLE, EXPORT_HEADER_STYLE } from '../../../utils/companyListExport';
import type { CompanyListFilters } from '../../../utils/companyListFilters';
import { createCompanyListFilters, filterCompanies } from '../../../utils/companyListFilters';
import { JWTHeader } from '../../../utils/JWTHeader';
import Badge from '../../common/Badge';
import MUITable from '../../common/MUITable';
import { showToast } from '../../common/Notification';
import Spinner from '../../common/Spinner';
import { ActivityBadges, CompanyListFiltersPanel, FederationLegend, FederationLogos } from '../components/CompanyListFilters';
import CardHolderModal from './components/CardHolderModal';

const HEADERS = [
  { title: 'NOM', class: 'w-2/12' },
  { title: 'SIREN', class: 'w-1/12' },
  { title: 'VILLE', class: 'w-1/12' },
  { title: 'FÉDÉRATION', class: 'w-1/12' },
  { title: 'ACTIVITÉ', class: 'w-1/12' },
  { title: 'NB LOTS HAB', class: 'w-1/12' },
  { title: 'NB LOTS HAB / BUR / COM', class: 'w-1/12' },
  { title: 'AGENTS CO', class: 'w-1/12' },
  { title: 'SALARIÉS', class: 'w-1/12' },
  { title: 'CARTE', class: 'w-1/12' },
  { title: 'DATE FIN CARTE', class: 'w-1/12' },
];

const SORTABLE_COLUMNS = [
  'NOM', 'SIREN', 'VILLE', 'NB LOTS HAB', 'NB LOTS HAB / BUR / COM', 'AGENTS CO', 'SALARIÉS', 'DATE FIN CARTE',
];

const NUMBER_FORMAT = new Intl.NumberFormat('fr-FR');

/**
 * Holders of a professional card in the CCI file, in or out of our base, with the filters of the company list.
 * The ~45 000 rows come in one request and are filtered in the browser, like the company list.
 */
function CardHolderList() {
  const jwt = useSelector((state: RootState) => state.auth.token);
  const [holders, setHolders] = useState<CardHolderListItem[]>([]);
  const [dataDate, setDataDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [filters, setFilters] = useState<CompanyListFilters>(createCompanyListFilters);
  const [selected, setSelected] = useState<CardHolderListItem | null>(null);
  const [exporting, setExporting] = useState(false);
  const lastTrigger = useRef<HTMLElement | null>(null);

  const fetchHolders = useCallback(async(signal: AbortSignal) => {
    setLoading(true);
    setLoadError(false);
    try {
      const response = await fetch('/api/admin/card-holders', { ...JWTHeader(jwt), signal });
      if (!response.ok) {
        throw new Error(`HTTP error status: ${response.status}`);
      }
      const data: CardHolderListResponse = await response.json();
      setHolders(toCardHolderListItems(data));
      setDataDate(data.dataDate);
    } catch (error) {
      if (signal.aborted) {return;}
      console.error('Error fetching card holders :', error);
      setLoadError(true);
    } finally {
      if (!signal.aborted) {
        setLoading(false);
      }
    }
  }, [jwt]);

  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (!jwt) {
      setLoading(false);
      setLoadError(true);
      return undefined;
    }
    const controller = new AbortController();
    fetchHolders(controller.signal);
    return () => controller.abort();
  }, [jwt, attempt, fetchHolders]);

  const filteredHolders = useMemo(() => filterCompanies(holders, filters), [holders, filters]);

  const openHolder = useCallback((holder: CardHolderListItem, trigger: HTMLElement) => {
    lastTrigger.current = trigger;
    setSelected(holder);
  }, []);

  const closeHolder = useCallback(() => {
    setSelected(null);
    lastTrigger.current?.focus();
  }, []);

  // Table rows are built once per load: a filter change then only picks rows among the ~45 000
  const rowsByHolder = useMemo(() => new Map(holders.map((holder) => [holder, {
    NOM: (
      <button
        type="button"
        onClick={(e) => openHolder(holder, e.currentTarget)}
        className="text-left font-medium text-rgpd-blue-dark underline-offset-2 hover:underline focus-visible:underline"
      >
        {holder.name}
      </button>
    ),
    SIREN: holder.siren ?? '',
    VILLE: [holder.postalCode, holder.city].filter(Boolean).join(' '),
    FÉDÉRATION: <FederationLogos details={holder.details} />,
    ACTIVITÉ: <ActivityBadges details={holder.details} />,
    'NB LOTS HAB': NUMBER_FORMAT.format(holder.nbLotsHab),
    'NB LOTS HAB / BUR / COM': NUMBER_FORMAT.format(holder.nbLotsTotal),
    'AGENTS CO': NUMBER_FORMAT.format(holder.nbAgentsCo),
    SALARIÉS: NUMBER_FORMAT.format(holder.nbSalaries),
    CARTE: holder.details.carte ?? '',
    'DATE FIN CARTE': formatIsoDay(holder.details.dateFinCarte),
  }])), [holders, openHolder]);

  const formattedData = useMemo(
    () => filteredHolders.map((holder) => rowsByHolder.get(holder)),
    [filteredHolders, rowsByHolder],
  );

  const exportToExcel = async() => {
    if (exporting) {return;}
    if (filteredHolders.length === 0) {
      showToast('warning', 'Aucune donnée à exporter. Veuillez ajuster vos filtres.');
      return;
    }

    setExporting(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Base des cartes pro');
      worksheet.addRow(CARD_HOLDER_EXPORT_HEADERS).eachCell((cell) => Object.assign(cell, EXPORT_HEADER_STYLE));
      filteredHolders.forEach((holder) => {
        worksheet.addRow(toCardHolderExportRow(holder)).eachCell((cell) => Object.assign(cell, EXPORT_CELL_STYLE));
      });
      worksheet.columns.forEach((column, index) => {
        column.width = CARD_HOLDER_EXPORT_WIDTHS[index] || 20;
      });

      await downloadWorkbook(workbook, `Base_cartes_pro_${new Date().toISOString()}.xlsx`);
      showToast('success', 'Export réussi.');
    } catch (error) {
      console.error('Error exporting card holders to Excel :', error);
      showToast('error', 'Une erreur est survenue lors de l\'export.');
    } finally {
      setExporting(false);
    }
  };

  const renderResults = () => {
    if (loading) {
      return (
        <div className="flex h-64 flex-col items-center justify-center" role="status">
          <Spinner size="xl" />
          <p className="mt-4 text-center text-sm text-gray-600">Chargement de la base des cartes…</p>
        </div>
      );
    }

    if (loadError) {
      return (
        <div className="flex h-64 flex-col items-center justify-center gap-4" role="alert">
          <p className="text-gray-800">La base des cartes n'a pas pu être chargée.</p>
          <button
            type="button"
            onClick={() => setAttempt((value) => value + 1)}
            className="flex items-center rounded bg-rgpd-blue-dark px-4 py-2 text-white transition-colors hover:bg-rgpd-blue-darker"
          >
            <ArrowPathIcon className="mr-2 h-5 w-5" />
            Réessayer
          </button>
        </div>
      );
    }

    if (filteredHolders.length === 0) {
      return (
        <div className="flex h-64 items-center justify-center">
          <Badge
            mode="warning"
            text="Aucune entreprise ne correspond aux critères sélectionnés."
            size="large"
            type="solid"
          />
        </div>
      );
    }

    return (
      <>
        <p className="mb-2 text-sm text-gray-600" aria-live="polite">
          {filteredHolders.length.toLocaleString('fr-FR')} entreprise{filteredHolders.length > 1 ? 's' : ''} sur {holders.length.toLocaleString('fr-FR')}
        </p>
        <MUITable
          data={formattedData}
          headers={HEADERS}
          tableClass="w-full"
          isSearchable
          sortableColumns={SORTABLE_COLUMNS}
        />
      </>
    );
  };

  return (
    <div>
      <div className="rounded-lg bg-white p-4 shadow-md">
        <h1 className="mb-4 flex items-center text-2xl font-bold text-gray-800">
          <BuildingStorefrontIcon className="mr-2 h-8 w-8 text-rgpd-blue-dark" />
          Base des cartes pro
        </h1>

        <div className="mb-4 max-w-4xl space-y-1 text-sm text-gray-600">
          <p>
            Entreprises titulaires d'une carte professionnelle d'agent immobilier selon le fichier des CCI,
            qu'elles soient ou non dans Croissance. Cliquez sur un nom pour voir ses informations.
          </p>
          {dataDate && (
            <p>
              Données CCI du {formatIsoDay(dataDate)} : une carte expirée à cette date a pu être renouvelée depuis.
            </p>
          )}
          <p>Note : Les données concernant le nombre de lots proviennent directement du registre national d'immatriculation.</p>
          <p>
            Les agents commerciaux et salariés titulaires d'une attestation (ADC) en cours de validité sont comptés
            sur l'entreprise dont ils dépendent.
          </p>
        </div>

        <FederationLegend />

        <div className="mt-8 mb-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Filtres</h2>
          <button
            type="button"
            onClick={exportToExcel}
            className="flex items-center rounded bg-rgpd-green px-4 py-2 text-white transition-colors hover:bg-rgpd-green-dark disabled:cursor-not-allowed disabled:opacity-50"
            disabled={loading || loadError || exporting}
          >
            {exporting ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Exportation en cours...
              </>
            ) : (
              <>
                <ArrowDownTrayIcon className="mr-2 h-5 w-5" />
                Exporter la liste
              </>
            )}
          </button>
        </div>
        <CompanyListFiltersPanel filters={filters} onChange={setFilters} />

        {renderResults()}
      </div>

      <CardHolderModal key={selected?.id} holder={selected} dataDate={dataDate} onClose={closeHolder} />
    </div>
  );
}

export default CardHolderList;
